import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTestData, defaultTests, getTestLabel, isTestAvailable } from "context/TestDataContext";
import { testSections, multiSectionKeys, sectionsForKey, resolveSectionLabel } from "config/testSections";
import { useTranslation } from "i18n/LanguageContext";
import { useState, useEffect } from "react";

export default function ProfileDialog({ open, handleClose, editingIndex }) {
  const { profiles, addProfile, updateProfile, deleteProfile } = useTestData();
  const { t, lang } = useTranslation();
  const isEditing = editingIndex !== null;
  const existing = isEditing ? profiles[editingIndex] : null;

  const [name, setName] = useState("");
  const [selectedTests, setSelectedTests] = useState([]);
  const [sectionTests, setSectionTests] = useState({});
  const [templateIndex, setTemplateIndex] = useState("");

  const buildSectionTests = (source) => {
    const initial = {};
    for (const key of multiSectionKeys) {
      if (source?.sectionTests?.[key]) {
        initial[key] = [...source.sectionTests[key]];
      } else if (source?.tests?.includes(key)) {
        initial[key] = sectionsForKey(key);
      } else {
        initial[key] = [];
      }
    }
    return initial;
  };

  useEffect(() => {
    if (open) {
      setName(existing ? existing.name : "");
      setSelectedTests(existing ? [...existing.tests] : []);
      setSectionTests(buildSectionTests(existing));
      setTemplateIndex("");
    }
  }, [open, existing]);

  const applyTemplate = (index) => {
    setTemplateIndex(index);
    if (index === "") {
      setSelectedTests([]);
      setSectionTests(buildSectionTests(null));
      return;
    }
    const template = profiles[index];
    setSelectedTests([...template.tests]);
    setSectionTests(buildSectionTests(template));
  };

  const isMultiSection = (key) => multiSectionKeys.includes(key);

  const isChecked = (key, sectionLabel) => {
    if (isMultiSection(key)) {
      return (sectionTests[key] || []).includes(sectionLabel);
    }
    return selectedTests.includes(key);
  };

  const toggleTest = (key, sectionLabel) => {
    if (isMultiSection(key)) {
      const current = sectionTests[key] || [];
      const next = current.includes(sectionLabel)
        ? current.filter((s) => s !== sectionLabel)
        : [...current, sectionLabel];
      setSectionTests((prev) => ({ ...prev, [key]: next }));
      setSelectedTests((prev) => {
        if (next.length > 0) return prev.includes(key) ? prev : [...prev, key];
        return prev.filter((t) => t !== key);
      });
      return;
    }
    setSelectedTests((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
    );
  };

  const handleSave = () => {
    const cleanedSectionTests = {};
    for (const key of multiSectionKeys) {
      const sections = sectionTests[key] || [];
      if (sections.length > 0) cleanedSectionTests[key] = sections;
    }
    const tests = selectedTests.filter((k) => {
      if (!isMultiSection(k)) return true;
      return (cleanedSectionTests[k] || []).length > 0;
    });
    const profile = { name: name.trim(), lang, tests, sectionTests: cleanedSectionTests };
    if (isEditing) {
      updateProfile(editingIndex, profile);
    } else {
      addProfile(profile);
    }
    handleClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="profile-dialog-title"
    >
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar>
          <Typography
            variant="h6"
            color="secondary"
            sx={{ flexGrow: 1 }}
            id="profile-dialog-title"
          >
            {isEditing ? t("profile.edit") : t("profile.new")}
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <HighlightOffRoundedIcon color="secondary" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box mx={3} my={2}>
        <Stack spacing={2}>
          <TextField
            label={t("profile.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />

          {!isEditing && profiles.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel id="profile-template-label">
                {t("profile.useTemplate")}
              </InputLabel>
              <Select
                labelId="profile-template-label"
                label={t("profile.useTemplate")}
                value={templateIndex}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <MenuItem value="">{t("profile.noTemplate")}</MenuItem>
                {profiles.map((p, idx) => (
                  <MenuItem key={p.name} value={idx}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Typography variant="subtitle2" color="textSecondary">
            {t("profile.tests")}
          </Typography>

          <Stack spacing={1}>
            {testSections.filter((s) => s.label !== "Autres").map((section) => {
              const sectionKeys = section.keys.filter(
                (k) => defaultTests[k] && isTestAvailable(defaultTests[k], lang)
              );
              if (sectionKeys.length === 0) return null;
              const allSelected = sectionKeys.every((k) => isChecked(k, section.label));
              const someSelected = sectionKeys.some((k) => isChecked(k, section.label));
              const toggleSection = () => {
                if (allSelected) {
                  sectionKeys.forEach((k) => {
                    if (isChecked(k, section.label)) toggleTest(k, section.label);
                  });
                } else {
                  sectionKeys.forEach((k) => {
                    if (!isChecked(k, section.label)) toggleTest(k, section.label);
                  });
                }
              };
              return (
                <Box key={section.label}>
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={toggleSection}
                      size="small"
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
                      {resolveSectionLabel(section, t)}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 0.5 }} />
                  {sectionKeys.map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={isChecked(key, section.label)}
                          onChange={() => toggleTest(key, section.label)}
                          size="small"
                        />
                      }
                      label={getTestLabel(key, defaultTests[key], lang)}
                      sx={{ display: "flex", ml: 2 }}
                    />
                  ))}
                </Box>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!name.trim() || selectedTests.length === 0}
              sx={{ flexGrow: 1 }}
            >
              {isEditing ? t("common.save") : t("common.add")}
            </Button>
            {isEditing && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => { deleteProfile(editingIndex); handleClose(); }}
              >
                {t("common.delete")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
